import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Loader2, Warehouse } from 'lucide-react';
import { callOffAPI } from '../../../lib/api';
import type { StockPlanningItem, WizardDelivery } from '../../../lib/api';
import { Dialog, DialogContent, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import WarehouseDeliveryToolbar from './WarehouseDeliveryToolbar';
import WarehouseDeliveryGrid, { PRIORITY_SORT_COLUMN } from './WarehouseDeliveryGrid';
import { useCommodityTypeOptions } from './useCommodityTypeOptions';
import { DEFAULT_WAREHOUSE_COLUMNS } from './DirectDeliveryColumns';
import { applyWarehouseLabels, effectiveCallOffCap, useLocalColumns } from './warehouseWizardGrid';
import { useTableSort } from '../../../hooks/useTableSort';
import { useCallOffConfig } from '../../../hooks/useCallOffConfig';
import ColourKey from '../shared/ColourKey';
import { WAREHOUSE_GRID_COLOUR_KEY } from './warehouseGridColours';
import {
  matchesPartPrefixFilter,
  matchesPartSearch,
  type PartPrefixFilter,
} from '../shared/partPrefixFilter';

export interface WizardWarehouse {
  id: number;
  name: string;
  code: string;
  warehouse_type: string;
}

interface WarehouseDeliveryModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (delivery: WizardDelivery) => void;
  supplierId: number;
  existingFillPercent: number;
  editDelivery?: WizardDelivery;
  warehouses: WizardWarehouse[];
  /** CR-6293: open-PO availability window (days), lifted to the wizard so it
   *  persists on the call-off. Defaults to the stock-planning value. */
  openPoDays: number;
  setOpenPoDays: (days: number) => void;
  /** The wizard's pickup date (YYYY-MM-DD); a later pickup extends the window. */
  pickupDate?: string;
  /** CR-7342: lifted setter so the date can be changed without closing the
   *  modal. Availability re-fetches on change (pickupDate is a fetch dep). */
  setPickupDate?: (date: string) => void;
  /** CR-8191: SAL "committed by" cut-off (YYYY-MM-DD), lifted to the wizard so
   *  every warehouse delivery in it uses one date. Defaults to the stock-planning
   *  value (SAL Committed End Date). */
  committedByDate: string;
  setCommittedByDate: (date: string) => void;
}

export default function WarehouseDeliveryModal({
  open, onClose, onAdd, supplierId, existingFillPercent, editDelivery, warehouses,
  openPoDays, setOpenPoDays, pickupDate, setPickupDate, committedByDate, setCommittedByDate,
}: WarehouseDeliveryModalProps) {
  // Empty string = no selection. The user must explicitly pick a warehouse;
  // this prevents accidentally saving against the first id in the list.
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>('');
  const [stockItems, setStockItems] = useState<StockPlanningItem[]>([]);
  const [loading, setLoading] = useState(false);
  // Client-side row filters. "partFilter" mirrors the supplier-side Current
  // Stock filter (hide, or show only, rows whose part number/description
  // starts with "part"). "commodityFilter" narrows to a single commodity type
  // ("all" = no filter). CR-7717: "searchTerm" matches part number or
  // description, so a buyer can find a part in a ~950-row grid without
  // scrolling for it.
  const [partFilter, setPartFilter] = useState<PartPrefixFilter>('all');
  const [commodityFilter, setCommodityFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  // Qty already committed for this delivery line at edit-mode entry. The
  // backend's PO-availability number already counts this against committed,
  // so we add it back to "effectiveAvailable" — otherwise editing a saved
  // line would always look over-limit.
  const [originalQty, setOriginalQty] = useState<Record<number, number>>({});
  // CR-8613: the Priority tick per row, for this call-off only. Rows already
  // on an ASN (edit mode) keep theirs: the supplier is picking from it.
  const [priorities, setPriorities] = useState<Record<number, boolean>>({});
  const [lockedPriority, setLockedPriority] = useState<Record<number, boolean>>({});
  const hasPrefilledRef = useRef(false);
  const { config } = useCallOffConfig();

  // Column management. The Saltney / Mold column labels are overridden at
  // render time with the customer's warehouse column labels so the same
  // column ID renders different copy per tenant without invalidating stored
  // column preferences (CR-7744: Mold is dropped with no secondary warehouse).
  const { columns: rawColumns, visible: rawVisibleCols, toggle: toggleCol, moveUp: moveColUp } = useLocalColumns(DEFAULT_WAREHOUSE_COLUMNS);
  const columns = useMemo(() => applyWarehouseLabels(rawColumns, config), [rawColumns, config]);
  const visibleCols = useMemo(() => applyWarehouseLabels(rawVisibleCols, config), [rawVisibleCols, config]);

  // CR-8191: each change of a window input starts a new fetch, and Chrome
  // reports one for every digit of a typed year. Only the newest may land, or
  // a slow earlier reply leaves the grid on a date that is no longer on screen.
  const fetchSeqRef = useRef(0);
  const fetchStockPlanning = useCallback(async () => {
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    try {
      // Page through every part for this supplier. A single page_size=500 fetch
      // silently dropped parts for suppliers with more than 500 assignments
      // (e.g. DREWAR has ~950), so the delivery grid was missing rows. Loop
      // until we've collected total_count items.
      const PAGE_SIZE = 500;
      const MAX_PAGES = 50; // safety stop (~25k parts) against a bad total_count
      // CR-6293: pass the call-off's window so Available To Call Off reflects
      // PO due within max(today + openPoDays, pickupDate).
      // CR-8191: and the call-off's committed-by date, so Committed By Date and
      // the figures built on it count SAL commitments up to the date on screen.
      const windowParams: Record<string, string | number> = {
        open_po_days: openPoDays,
        sal_committed_end_date: committedByDate,
      };
      if (pickupDate) windowParams['pickup_date'] = pickupDate;
      const first = await callOffAPI.getStockPlanning({
        supplier_id: supplierId,
        page_size: PAGE_SIZE,
        page: 1,
        ...windowParams,
      });
      let items = first.data.items || [];
      const total = first.data.total_count || items.length;
      let page = 2;
      while (items.length < total && page <= MAX_PAGES) {
        const res = await callOffAPI.getStockPlanning({
          supplier_id: supplierId,
          page_size: PAGE_SIZE,
          page,
          ...windowParams,
        });
        const batch = res.data.items || [];
        if (batch.length === 0) break;
        items = items.concat(batch);
        page += 1;
      }
      if (seq !== fetchSeqRef.current) return;
      setStockItems(items);
    } catch (err: unknown) {
      if (seq !== fetchSeqRef.current) return;
      const message = err instanceof Error ? err.message : 'Failed to load stock planning data';
      toast.error(message);
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, [supplierId, openPoDays, pickupDate, committedByDate]);

  useEffect(() => {
    if (open) { hasPrefilledRef.current = false; }
  }, [open]);

  useEffect(() => {
    if (open) { fetchStockPlanning(); }
  }, [open, fetchStockPlanning]);

  // Pre-fill quantities when editing
  useEffect(() => {
    if (!open || hasPrefilledRef.current || stockItems.length === 0) return;
    if (editDelivery) {
      const qtyMap: Record<number, number> = {};
      const priorityMap: Record<number, boolean> = {};
      const lockedMap: Record<number, boolean> = {};
      const pnToStockId = new Map<string, number>();
      for (const item of stockItems) {
        pnToStockId.set(item.part_number, item.id);
      }
      for (const part of editDelivery.parts) {
        const stockId = part.partId !== null && stockItems.some(s => s.id === part.partId)
          ? part.partId
          : pnToStockId.get(part.partNumber);
        if (stockId === undefined) continue;
        qtyMap[stockId] = part.qty;
        if (part.isPriority) priorityMap[stockId] = true;
        if (part.priorityLocked) lockedMap[stockId] = true;
      }
      setQuantities(qtyMap);
      setOriginalQty(qtyMap);
      setPriorities(priorityMap);
      setLockedPriority(lockedMap);
      // Only prefill the dropdown if the existing destination is still a
      // valid active warehouse for this customer. If it's stale (e.g. an old
      // soft-deleted warehouse), leave it empty so the user is forced to
      // re-pick — no silent rewriting to a default.
      const valid = editDelivery.destinationId != null
        && warehouses.some(w => w.id === editDelivery.destinationId);
      setSelectedWarehouse(valid ? String(editDelivery.destinationId) : '');
      hasPrefilledRef.current = true;
    } else {
      setQuantities({});
      setOriginalQty({});
      setPriorities({});
      setLockedPriority({});
      hasPrefilledRef.current = true;
    }
  }, [open, editDelivery, stockItems]);

  const handlePriorityChange = (itemId: number, checked: boolean) => {
    if (lockedPriority[itemId]) return;
    setPriorities(prev => ({ ...prev, [itemId]: checked }));
  };

  const handleQtyChange = (itemId: number, value: string) => {
    const numValue = parseInt(value, 10);
    if (isNaN(numValue) || numValue <= 0) {
      setQuantities(prev => {
        const updated = { ...prev };
        delete updated[itemId];
        return updated;
      });
    } else {
      setQuantities(prev => ({ ...prev, [itemId]: numValue }));
    }
  };

  // Commodity types carried by the loaded parts. Merged with the configured
  // list in the hook below, so the dropdown offers every commodity type rather
  // than only those that happen to appear on screen.
  const commodityTypesInRows = useMemo(() => {
    const set = new Set<string>();
    for (const it of stockItems) {
      if (it.commodity_type) set.add(it.commodity_type);
    }
    return Array.from(set);
  }, [stockItems]);

  const commodityOptions = useCommodityTypeOptions(commodityTypesInRows);

  // Rows after applying the search, Parts and commodity-type filters.
  const visibleItems = useMemo(
    () => stockItems.filter(item => {
      if (!matchesPartSearch(searchTerm, item.part_number, item.description)) return false;
      if (!matchesPartPrefixFilter(partFilter, item.part_number, item.description)) return false;
      if (commodityFilter !== 'all' && (item.commodity_type || '') !== commodityFilter) return false;
      return true;
    }),
    [stockItems, searchTerm, partFilter, commodityFilter]
  );

  // The Priority column sorts like any other. The tick is read as 1 or 0, so
  // a first click (numeric, high→low) brings ticked rows to the top.
  const sortableItems = useMemo(
    () => visibleItems.map(item => ({
      ...item, [PRIORITY_SORT_COLUMN]: priorities[item.id] ? 1 : 0,
    })),
    [visibleItems, priorities]
  );

  // CR-6296: spreadsheet-style sort of the delivery grid. Double-click a column
  // (e.g. Stock Health Min) to float negative/deficit parts to the top so they
  // get called off first. Client-side over every loaded part. Row keys are the
  // stable item.id, so reordering never loses a typed Qty.
  const {
    sortColumn, sortDirection, sortedItems: sortedVisibleItems,
    handleHeaderClick, handleHeaderDoubleClick, getAriaSort,
  } = useTableSort<StockPlanningItem>(sortableItems);

  const selectedItems = useMemo(
    () => stockItems.filter(item => quantities[item.id] > 0),
    [stockItems, quantities]
  );

  const modalFillPercent = useMemo(() => {
    let fill = 0;
    for (const item of selectedItems) {
      const qty = quantities[item.id] || 0;
      const loadQty = parseFloat(item.load_qty) || 0;
      if (loadQty > 0) fill += qty / loadQty;
    }
    return Math.round(fill * 100);
  }, [selectedItems, quantities]);

  const totalFillPercent = existingFillPercent + modalFillPercent;
  const totalUnits = selectedItems.reduce((sum, item) => sum + (quantities[item.id] || 0), 0);

  // CR-7342: the cap now also honours stock the supplier has already produced.
  // The formula and the reasoning behind each term live in effectiveCallOffCap
  // so they can be unit-tested.
  const overInfo = useMemo(() => {
    const map: Record<number, { over: boolean; effective: number }> = {};
    for (const item of stockItems) {
      const effective = effectiveCallOffCap(item, originalQty[item.id] || 0);
      const qty = quantities[item.id] || 0;
      map[item.id] = { over: qty > 0 && qty > effective, effective };
    }
    return map;
  }, [stockItems, quantities, originalQty]);

  const overCount = selectedItems.filter(i => overInfo[i.id]?.over).length;
  const hasOverLimit = overCount > 0;

  const handleAdd = () => {
    const warehouse = warehouses.find(w => String(w.id) === selectedWarehouse);
    if (!warehouse) { toast.error('Please select a warehouse'); return; }
    if (selectedItems.length === 0) { toast.error('Please enter quantities for at least one item'); return; }
    // A quantity with no PO behind it cannot be called off. The backend rejects
    // it outright, so stop here with a readable message rather than letting the
    // wizard build a delivery the save will refuse.
    if (hasOverLimit) {
      toast.error(
        `${overCount} item${overCount !== 1 ? 's' : ''} exceed what is available to call off ` +
        'or held in supplier stock. ' +
        'Reduce the quantity to continue.'
      );
      return;
    }

    const delivery: WizardDelivery = {
      type: 'warehouse',
      destinationId: warehouse.id,
      destinationName: warehouse.name,
      salesOrderId: null,
      address: '',
      postcode: '',
      deliveryDate: '',
      parts: selectedItems.map(item => ({
        partId: item.id,
        partNumber: item.part_number,
        description: item.description,
        qty: quantities[item.id] || 0,
        palletQty: parseFloat(item.pallet_qty) || 0,
        loadQty: parseFloat(item.load_qty) || 0,
        // CR-8613: the tick ticked here, for this call-off only.
        isPriority: Boolean(priorities[item.id]),
        priorityLocked: Boolean(lockedPriority[item.id]),
      })),
    };
    onAdd(delivery);
  };

  const handleClose = () => {
    setQuantities({});
    setPriorities({});
    setLockedPriority({});
    setSearchTerm('');
    hasPrefilledRef.current = false;
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-[95vw] max-h-[95vh] w-full h-full flex flex-col p-0" aria-describedby={undefined}>
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b">
          <Warehouse className="h-5 w-5 text-red-500" />
          <DialogTitle className="text-lg font-semibold">
            {editDelivery ? 'Edit Warehouse Delivery' : 'Add Warehouse Delivery'}
          </DialogTitle>
        </div>

        <WarehouseDeliveryToolbar
          warehouses={warehouses}
          selectedWarehouse={selectedWarehouse}
          setSelectedWarehouse={setSelectedWarehouse}
          columns={columns}
          onToggleColumn={toggleCol}
          onMoveColumnUp={moveColUp}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          partFilter={partFilter}
          setPartFilter={setPartFilter}
          commodityFilter={commodityFilter}
          setCommodityFilter={setCommodityFilter}
          commodityOptions={commodityOptions}
          openPoDays={openPoDays}
          setOpenPoDays={setOpenPoDays}
          pickupDate={pickupDate}
          setPickupDate={setPickupDate}
          committedByDate={committedByDate}
          setCommittedByDate={setCommittedByDate}
          totalFillPercent={totalFillPercent}
          existingFillPercent={existingFillPercent}
          modalFillPercent={modalFillPercent}
          isEdit={Boolean(editDelivery)}
        />

        {/* CR-8613: what the grid's colours mean, outside the scroll. */}
        <ColourKey entries={WAREHOUSE_GRID_COLOUR_KEY} className="px-6 pt-2" />

        {/* Stock grid */}
        <div className="flex-1 overflow-auto px-6 py-2">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : stockItems.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground">
              No stock planning data found for this supplier.
            </div>
          ) : visibleItems.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground text-sm">
              {searchTerm.trim()
                ? `No parts match “${searchTerm.trim()}” with the active filters.`
                : `All ${stockItems.length} parts were hidden by the active filters.`}
            </div>
          ) : (
            <WarehouseDeliveryGrid
              columns={visibleCols}
              items={sortedVisibleItems}
              quantities={quantities}
              onQtyChange={handleQtyChange}
              priorities={priorities}
              lockedPriority={lockedPriority}
              onPriorityChange={handlePriorityChange}
              overInfo={overInfo}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onHeaderClick={handleHeaderClick}
              onHeaderDoubleClick={handleHeaderDoubleClick}
              getAriaSort={getAriaSort}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-3 border-t bg-muted/30">
          <div className="text-sm text-muted-foreground">
            {selectedItems.length} {selectedItems.length === 1 ? 'item' : 'items'} selected &middot;{' '}
            {totalUnits} units
          </div>
          <div className="flex items-center gap-2">
            {hasOverLimit && (
              <span className="text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-full px-2.5 py-1">
                {overCount} item{overCount !== 1 ? 's' : ''} over the quantity that can be called off — reduce to continue
              </span>
            )}
            <Button variant="outline" onClick={handleClose}>Cancel</Button>
            <Button
              onClick={handleAdd}
              disabled={selectedItems.length === 0 || !selectedWarehouse || hasOverLimit}
              title={
                !selectedWarehouse
                  ? 'Select a warehouse first'
                  : hasOverLimit
                    ? 'One or more quantities exceed what is available to call off or held in supplier stock'
                    : undefined
              }
            >
              {editDelivery ? 'Update Delivery' : 'Add Delivery to Call-Off'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
